import {
  getMappedCategories,
  productCategoriesMapping,
  stagingCategoriesMapping,
} from '@ouster/webviz-react-components';

import LZString from 'lz-string';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { parseUrlParams } from '../../utils/driveUtil';

type GroupFilterItem = {
  [key: string]: string[];
};

const isProduction = window.location.hostname === 'studio.ouster.com';

const categoryMapping = isProduction
  ? productCategoriesMapping
  : stagingCategoriesMapping;

const useFilters = (
  defaultOrderBy = '',
  defaultSortOrder: 'asc' | 'desc' | '' = ''
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHydrated, setIsHydrated] = useState(false);
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  useEffect(() => {
    let updated = false;
    const params = new URLSearchParams(location.search);
    const currentOrderBy = params.get('order_by')?.trim() || '';
    const currentSortOrder = params.get('sort_order')?.trim() || '';
    const currentPage = params.get('page')?.trim() || '';

    if (!currentOrderBy && defaultOrderBy) {
      params.set('order_by', defaultOrderBy);
      updated = true;
    }

    if (
      (!currentSortOrder || !['asc', 'desc'].includes(currentSortOrder)) &&
      defaultSortOrder
    ) {
      params.set('sort_order', defaultSortOrder);
      updated = true;
    }

    const parsedPage = parseInt(currentPage, 10);
    if (!currentPage || !Number.isFinite(parsedPage) || parsedPage <= 0) {
      params.set('page', '1');
      updated = true;
    }

    if (updated) {
      setIsHydrated(false);
      navigate({ search: params.toString() }, { replace: true });
      return;
    }

    setIsHydrated(true);
  }, [defaultOrderBy, defaultSortOrder, location.search, navigate]);

  // Memoize filters to avoid recomputation on every render
  const filters = useMemo(
    () => parseUrlParams(location.search),
    [location.search]
  );

  const orderBy = searchParams.get('order_by') || '';
  const sortOrder =
    (searchParams.get('sort_order') as 'asc' | 'desc' | null) || '';
  const pageIndex = useMemo(() => {
    const page = parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(page) && page > 0 ? page - 1 : 0;
  }, [searchParams]);

  const setPageIndex = (newPageIndex: number) => {
    if (!Number.isInteger(newPageIndex) || newPageIndex < 0) return;

    const params = new URLSearchParams(location.search);
    params.set('page', `${newPageIndex + 1}`);

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;
    if (nextSearch === currentSearch) return;

    navigate({ search: nextSearch });
  };

  const setOrderBy = (newOrderBy: string, newSortOrder: 'asc' | 'desc') => {
    if (orderBy === newOrderBy && sortOrder === newSortOrder) return;

    const params = new URLSearchParams(location.search);
    params.set('order_by', newOrderBy);
    params.set('sort_order', newSortOrder);

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;
    if (nextSearch === currentSearch) return;

    navigate({ search: nextSearch });
  };

  // Function to compress and update URL params
  const updateFiltersInUrl = (
    updatedFilters: Record<string, string[]>,
    currentOrderBy?: string,
    currentSortOrder?: string,
    currentPage?: string
  ) => {
    const compressedFilters = LZString.compressToEncodedURIComponent(
      JSON.stringify(updatedFilters)
    );

    const params = new URLSearchParams();
    params.set('filters', compressedFilters);
    if (currentOrderBy) {
      params.set('order_by', currentOrderBy);
    }
    if (currentSortOrder) {
      params.set('sort_order', currentSortOrder);
    }
    if (currentPage) {
      params.set('page', currentPage);
    }

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;
    if (nextSearch === currentSearch) return;

    navigate({ search: nextSearch });
  };

  // Set individual filter
  const setFilter = (key: string, values: string[]) => {
    const params = new URLSearchParams(location.search);
    const currentOrderBy = params.get('order_by') ?? undefined;
    const currentSortOrder = params.get('sort_order') ?? undefined;
    const currentPage = params.get('page') ?? undefined;

    const updatedFilters = {
      ...filters,
      [key]: values,
    };
    updateFiltersInUrl(
      updatedFilters,
      currentOrderBy,
      currentSortOrder,
      currentPage
    );
  };

  const decompressExistedFilter = (
    currentCompressedFilters: string | null
  ): Record<string, string[]> => {
    if (currentCompressedFilters) {
      try {
        const decompressedFilters = LZString.decompressFromEncodedURIComponent(
          currentCompressedFilters
        );
        if (decompressedFilters) {
          return JSON.parse(decompressedFilters) as Record<string, string[]>;
        }
        return {};
      } catch (error) {
        console.error('Error decompressing filters:', error);
        return {};
      }
    }
    return {};
  };

  const setGroupFilters = (groupFilterItems: GroupFilterItem[]) => {
    // Here we need to preserve previous filters
    const params = new URLSearchParams(location.search);

    const currentOrderBy = params.get('order_by') ?? undefined;
    const currentSortOrder = params.get('sort_order') ?? undefined;
    const currentPage = params.get('page') ?? undefined;
    const currentCompressedFilters = params.get('filters');

    const currentFilters: Record<string, string[]> = {};

    // Decompress existing filters if available
    const prevFilters: Record<string, string[]> = decompressExistedFilter(
      currentCompressedFilters
    );

    // Append new group filters
    groupFilterItems.forEach((groupFilterItem) => {
      const [key] = Object.keys(groupFilterItem);
      const values = groupFilterItem[key];
      if (!currentFilters[key]) {
        currentFilters[key] = [];
      }
      currentFilters[key] = [...values];
    });

    /* 
      we are using short list of category but it needs to be mapped to full list
      for filtering. staging and product have the different mapping, we need to do it accordingly.
    */

    Object.keys(currentFilters).forEach((key) => {
      if (key === 'data_category') {
        const mappedCategories: string[] = [];

        currentFilters[key].forEach((cate) => {
          const categories = getMappedCategories(categoryMapping, cate);

          mappedCategories.push(...categories);
        });

        currentFilters[key] = [...mappedCategories];
      }
    });

    const newFilters = { ...prevFilters, ...currentFilters };

    updateFiltersInUrl(
      newFilters,
      currentOrderBy,
      currentSortOrder,
      currentPage
    );
  };

  return {
    filters,
    orderBy,
    sortOrder,
    pageIndex,
    isHydrated,
    setOrderBy,
    setPageIndex,
    setFilter,
    setGroupFilters,
  };
};

export default useFilters;
